import {
  type ReactElement,
  isValidElement,
  useCallback,
  useMemo,
  useState,
} from "react";
import type { IStepProps } from "src/shared/forms/steps/Step";

export type StepsNavigationReturn = ReturnType<typeof useStepsNavigation>;

export const useStepsNavigation = (
  children: Array<ReactElement<IStepProps> | null | undefined | boolean>,
) => {
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);

  const isValidStep = useCallback(
    (stepIndex: number): boolean => {
      if (stepIndex < 0) {
        return false;
      }
      if (!children[stepIndex]) {
        return false;
      }
      if (!isValidElement(children[stepIndex])) {
        return false;
      }
      return true;
    },
    [children],
  );

  const getCorrespondingValidStep = useCallback(
    (stepIndex: number, delta: number): number | null => {
      const correspondingStepIndex = stepIndex + delta;
      if (!children[correspondingStepIndex]) {
        if (
          correspondingStepIndex >= 0 &&
          correspondingStepIndex < children.length
        ) {
          return getCorrespondingValidStep(correspondingStepIndex, delta);
        }
        return null;
      }
      if (!isValidStep(correspondingStepIndex)) {
        return getCorrespondingValidStep(correspondingStepIndex, delta);
      }
      return correspondingStepIndex;
    },
    [children, isValidStep],
  );

  const previousValidStepIndex = useMemo(() => {
    return getCorrespondingValidStep(currentStepIndex, -1);
  }, [getCorrespondingValidStep, currentStepIndex]);

  const nextValidStepIndex = useMemo(() => {
    return getCorrespondingValidStep(currentStepIndex, 1);
  }, [getCorrespondingValidStep, currentStepIndex]);

  return {
    currentStepIndex,
    setCurrentStepIndex,
    previousValidStepIndex,
    nextValidStepIndex,
  };
};
